import { memo, type ReactNode } from 'react';

import { WorkspaceAgentBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/bubble/WorkspaceAgentBubble';
import { type WorkspaceAgentDefinitionField } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/data/types';
import { WorkspaceAgentDefinitionList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/data/WorkspaceAgentDefinitionList';
import {
  type WorkspaceAgentResultBullet,
  type WorkspaceAgentResultQuickReply,
  type WorkspaceAgentResultWork,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/types';
import { WorkspaceAgentResultMessageQuickReply } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/WorkspaceAgentResultMessageQuickReply';
import { WorkspaceAgentWorkDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/WorkspaceAgentWorkDisclosure';
import { WorkspaceAgentSummaryBullet } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/summary/WorkspaceAgentSummaryBullet';

/** How many metadata rows show before the rest collapse behind `Show more`. */
const OVERVIEW_PREVIEW_FIELD_COUNT = 4;

export type WorkspaceAgentResultMessageProps = {
  /** A node, not a string, so the panel's `react-markdown` stays the only markdown path. */
  body?: ReactNode;
  bodyTitle?: string;
  bullets?: WorkspaceAgentResultBullet[];
  'data-testid'?: string;
  /**
   * Structured evidence under the findings — a table of what a write would do, a metadata recap, the
   * artifacts a run produced. A node rather than a prop per shape, so a new kind of evidence does not
   * widen this component's contract.
   */
  detail?: ReactNode;
  onOpenQuote?: (id: string) => void;
  onOpenThumbnail?: (id: string) => void;
  onQuickReply?: (id: string) => void;
  overviewFields?: WorkspaceAgentDefinitionField[];
  quickReplies?: WorkspaceAgentResultQuickReply[];
  /** The run behind the answer, shown above it. Omitted when there is no work to disclose. */
  work?: WorkspaceAgentResultWork;
};

/**
 * A settled agent answer: what it did, what it found, and where you can go next.
 *
 * Only the reply itself sits in the bubble. The work disclosure above and the quick replies below
 * are about the exchange rather than part of the answer, so keeping them outside stops the bubble
 * from reading as though the agent said them.
 */
export const WorkspaceAgentResultMessage = memo(
  ({
    body,
    bodyTitle,
    bullets,
    'data-testid': testId,
    detail,
    onOpenQuote,
    onOpenThumbnail,
    onQuickReply,
    overviewFields,
    quickReplies,
    work,
  }: WorkspaceAgentResultMessageProps) => (
    <div className="flex w-full min-w-0 flex-col items-start gap-2" data-testid={testId}>
      {work ? <WorkspaceAgentWorkDisclosure label={work.label} phases={work.phases} /> : null}

      <WorkspaceAgentBubble variant="agent">
        {bodyTitle ? (
          <p className="mb-1 break-words font-semibold ads:text-primary text-14 text-gray-12">{bodyTitle}</p>
        ) : null}
        {body ? <div className="ads:text-primary text-14 text-gray-12 leading-5">{body}</div> : null}

        {bullets?.length ? (
          <div className="space-y-1 py-1">
            {bullets.map((bullet) => (
              <WorkspaceAgentSummaryBullet
                key={bullet.id}
                onOpenQuote={onOpenQuote}
                onOpenThumbnail={onOpenThumbnail}
                quotes={bullet.quotes}
                segments={bullet.segments}
                thumbnails={bullet.thumbnails}
              />
            ))}
          </div>
        ) : null}

        {detail ? <div className="pt-2">{detail}</div> : null}

        {overviewFields?.length ? (
          <div className="pt-2">
            <WorkspaceAgentDefinitionList
              fields={overviewFields}
              heading="Overview"
              previewCount={OVERVIEW_PREVIEW_FIELD_COUNT}
            />
          </div>
        ) : null}
      </WorkspaceAgentBubble>

      {onQuickReply && quickReplies?.length ? (
        <div className="flex flex-wrap gap-2">
          {quickReplies.map((quickReply) => (
            <WorkspaceAgentResultMessageQuickReply
              key={quickReply.id}
              onSelect={onQuickReply}
              quickReply={quickReply}
            />
          ))}
        </div>
      ) : null}
    </div>
  ),
);

WorkspaceAgentResultMessage.displayName = 'WorkspaceAgentResultMessage';
